import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createFileRoute } from '@tanstack/react-router';
import { Box, Button, Flex, Link, Spinner, Text, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { Action, ItemType } from '@/../../server/types/loggingTypes';
import { PrivacyLevel } from '@/../../shared/types/notesTypes';
import NoteTextarea from '@/components/NoteTextarea';
import { useAppSelector } from '@/store/hooks';
import { setAuthInfo } from '@/store/slices/authSlice';
import { getStatsigClient } from '@/utils/statsig';

export const Route = createFileRoute('/_privateRoutes/onboarding/answer-inviter-question')({
  component: AnswerInviterQuestion,
});

export default function AnswerInviterQuestion() {
  const [question, setQuestion] = useState<string>('');
  const [answer, setAnswer] = useState<string>('');
  const [isNotePublic, setIsNotePublic] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState<boolean>(true);

  const dispatch = useDispatch();
  const toast = useToast();
  const profilePic = useAppSelector((state) => state.auth.profilePic) || '';

  useEffect(() => {
    getStatsigClient().logEvent('inviter_question_page', Action.VIEW);

    const fetchQuestion = async () => {
      try {
        const response = await axios.get('/api/invite/getInviterQuestion');
        if (response.status === 200 && response.data.success) {
          setQuestion(response.data.data.question);
        } else {
          throw new Error(response.data.message || 'Failed to get question');
        }
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Failed to load question.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoadingQuestion(false);
      }
    };

    fetchQuestion();
  }, []);

  const handleAnswerChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAnswer(e.target.value);
  };

  const handleSubmit = async () => {
    getStatsigClient().logEvent('inviter_question_page', Action.CLICK, {
      item_name: 'submit',
      item_type: ItemType.BUTTON,
    });

    if (!answer.trim()) {
      toast({
        title: 'Error',
        description: 'Answer cannot be empty.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/invite/answerInviterQuestion', {
        answer: answer.trim(),
        privacyLevel: isNotePublic ? PrivacyLevel.PUBLIC : PrivacyLevel.CONNECTION_ONLY,
      });

      if (response.data.success) {
        localStorage.setItem('deferProfilePicUpload', new Date().toISOString());
        dispatch(setAuthInfo({ inviterQuestionAnswered: true, isOnBoardingTutorialComplete: true }));
      } else {
        throw new Error(response.data.message || 'Failed to submit your answer.');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to submit your answer.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box bg="black" minH="100vh" px={4} position="relative">
      <Flex direction="column" maxW="container.sm" mx="auto" pt={12} minH="100vh">
        {isLoadingQuestion ? (
          <Flex align="center" justify="center" flex={1}>
            <Spinner size="xl" color="white" />
          </Flex>
        ) : (
          <>
            <Text fontSize="xl" fontWeight="bold" color="white" mb={4}>
              {question}
            </Text>

            <NoteTextarea
              profilePic={profilePic}
              noteContent={answer}
              setNoteContent={handleAnswerChange}
              onSaveNote={handleSubmit}
              isNotePublic={isNotePublic}
              setIsNotePublic={setIsNotePublic}
              hideAvatar
            />

            <Button
              onClick={handleSubmit}
              isLoading={isSubmitting}
              bg="#FF0050"
              color="white"
              size="lg"
              height="60px"
              borderRadius="full"
              _hover={{ bg: '#df0045' }}
              fontSize="md"
              fontWeight="semibold"
              mt={4}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
            >
              Submit
            </Button>
          </>
        )}

        <Flex
          position="absolute"
          bottom={4}
          left={0}
          right={0}
          justify="center"
          w="full"
          gap={2}
          color="#545454"
          fontSize="10px"
          fontWeight="bold"
        >
          <Link
            href="https://docs.google.com/document/d/1p3-fJ25CafAI13qyOYEZMu4rCH95JVaXRfsNejTyDD0/edit?usp=sharing"
            _hover={{ textDecoration: 'underline' }}
          >
            Terms of Service
          </Link>
          <Text>•</Text>
          <Link
            href="https://docs.google.com/document/d/1zImTFVA7nfo3-ZcbCk6Hr1Y67W5P9GHEhi8PPaUAxdc/edit?usp=sharing"
            _hover={{ textDecoration: 'underline' }}
          >
            Privacy Policy
          </Link>
        </Flex>
      </Flex>
    </Box>
  );
}
